@if (settingsLoading()) {
	<div class="admin-feedback">
		<div class="status-text">Loading admin settings...</div>
	</div>
}
@if (settingsError()) {
	<div class="admin-feedback">
		<div class="error-banner" role="alert">
			<i class="bi bi-exclamation-octagon-fill"></i>
			<span class="error-banner-message">{{ settingsError() }}</span>
			<button
				type="button"
				class="error-banner-dismiss"
				(click)="clearSettingsError()"
				aria-label="Dismiss error"
			>
				✕
			</button>
		</div>
	</div>
}

<div class="admin-container">
	<!-- Sidebar Menu -->
	<aside class="admin-sidebar">
		<nav class="admin-nav">
			<button
				class="nav-item"
				[class.active]="activeMenu() === 'streaming'"
				(click)="setActiveMenu('streaming')"
			>
				<i class="bi bi-camera-video"></i>
				<span>Streaming</span>
			</button>
			@if (showStreamingEcsMenu()) {
				<button
					class="nav-item"
					[class.active]="activeMenu() === 'streaming-ecs'"
					(click)="setActiveMenu('streaming-ecs')"
				>
					<i class="bi bi-hdd-rack"></i>
					<span>Streaming RTMP Tasks</span>
				</button>
			}
			<button
				class="nav-item"
				[class.active]="activeMenu() === 'notification'"
				(click)="setActiveMenu('notification')"
			>
				<i class="bi bi-bell"></i>
				<span>Notification</span>
			</button>
		</nav>
	</aside>

	<!-- Main Content Area -->
	<main class="admin-content">
		<!-- STREAMING SECTION -->
		@if (activeMenu() === 'streaming') {
			<section class="content-section">
				<div class="section-header">
					<h1 class="section-title">Streaming Configuration</h1>
					<p class="section-description">
						Manage live video streams and streaming data configuration
					</p>
				</div>

				@if (settings()) {
					<!-- Video Stream Toggle -->
					<div class="settings-group">
						<div class="settings-group-header">
							<h2 class="settings-group-title">Video Stream</h2>
							<p class="settings-group-description">
								Enable or disable the live main/driver feed
							</p>
						</div>
						<div class="settings-group-content">
							<div class="flex items-center justify-between">
								<div class="flex flex-col gap-2">
									<span class="font-semibold">Live Video Feed</span>
									@if (settings()?.race?.disableStream) {
										<div class="stream-status-inactive">
											<i class="bi bi-record-circle"></i>
											<strong>STREAM INACTIVE</strong>
										</div>
									} @else {
										<div class="stream-status-active">
											<i class="bi bi-record-circle-fill"></i>
											<strong>STREAM ACTIVE</strong>
										</div>
									}
								</div>
								<vb-lxp-switch
									[checked]="driverStreamStatus()"
									[saveFn]="saveDriverStreamStatus"
								></vb-lxp-switch>
							</div>
						</div>
					</div>

					<div class="divider-line"></div>

					<!-- Streaming Table -->
					<div class="streaming-table-section">
						<div class="streaming-table-header">
							<div class="streaming-table-header-left">
								<h2 class="settings-group-title">Streaming Configurations</h2>
								<p class="settings-group-description">
									Upload and manage streaming data configuration
								</p>
							</div>
							<div class="streaming-table-header-actions">
								<input
									#fileInput
									type="file"
									accept=".json"
									hidden
									(change)="onFileSelected($event)"
								/>
								<button
									class="btn-upload"
									[disabled]="isUploadSaving()"
									(click)="browseFile()"
									title="Upload JSON configuration"
								>
									<i class="bi bi-upload"></i>
									<span>Upload</span>
								</button>
								@if (expectedJsonStructure()) {
									<button
										class="btn-clear"
										[disabled]="isUploadSaving()"
										(click)="clearStreamingData()"
										title="Clear all streaming data"
									>
										<i class="bi bi-trash"></i>
										<span>Clear</span>
									</button>
								}
							</div>
						</div>
						@if (uploadError()) {
							<div class="upload-error-banner">
								<i class="bi bi-exclamation-triangle-fill"></i>
								<span>{{ uploadError() }}</span>
							</div>
						}
						@if (uploadedFileName()) {
							<div class="upload-success-banner">
								<i class="bi bi-check-circle-fill"></i>
								<span>Uploaded: {{ uploadedFileName() }}</span>
							</div>
						}
						@if (expectedJsonStructure(); as streamingRows) {
							<vb-lxp-streaming-table [rows]="streamingRows"></vb-lxp-streaming-table>
						} @else {
							<div class="streaming-table-placeholder">
								<i class="bi bi-file-earmark-arrow-up text-5xl text-[var(--text-secondary)] opacity-50"></i>
								<p class="text-sm text-[var(--text-secondary)] mt-3">No streaming data uploaded</p>
								<p class="text-xs text-[var(--text-secondary)] opacity-70">Click Upload to add configuration</p>
							</div>
						}
					</div>
				}
			</section>
		}

		<!-- STREAMING ECS TASKS SECTION -->
		@if (showStreamingEcsMenu() && activeMenu() === 'streaming-ecs') {
			<section class="content-section">
				<div class="section-header">
					<h1 class="section-title">Streaming RTMP Tasks</h1>
					<p class="section-description">
						Manage and monitor streaming RTMP service tasks
					</p>
				</div>

				@if (streamingEcsLoading()) {
					<div class="settings-group">
						<div class="settings-group-content">
							<div class="placeholder-message">
								<i class="bi bi-arrow-repeat spinner text-4xl"></i>
								<p class="text-sm text-[var(--text-secondary)]">Loading streaming RTMP services...</p>
							</div>
						</div>
					</div>
				} @else {
					<div class="settings-group">
						<div class="settings-group-header">
							<h2 class="settings-group-title">RTMP Services</h2>
							<p class="settings-group-description">
								Start and stop streaming RTMP services
							</p>
						</div>
						<div class="settings-group-content">
							@if (streamingEcsServices()) {
								<vb-lxp-streaming-ecs-table 
									[services]="streamingEcsServices"
									(refreshServices)="loadStreamingEcsServices()"
								></vb-lxp-streaming-ecs-table>
							}
						</div>
					</div>
				}
			</section>
		}

		<!-- NOTIFICATION SECTION -->
		@if (activeMenu() === 'notification') {
			<section class="content-section">
				<div class="section-header">
					<h1 class="section-title">Notification Panel</h1>
					<p class="section-description">
						Broadcast messages to all connected users on the live site
					</p>
				</div>

				<div class="settings-group">
					<div class="settings-group-header">
						<h2 class="settings-group-title">Compose Message</h2>
						<p class="settings-group-description">
							Create and send notification messages to the live site
						</p>
					</div>
					<div class="settings-group-content">
						<div class="flex mb-3 items-center justify-between">
							<span class="text-sm font-semibold">Message Content</span>
							<span class="status-text">{{ notificationMessageLength() }} / 300</span>
						</div>
						<textarea
							[ngModel]="notificationMessageDraft()"
							(ngModelChange)="onNotificationMessageChanged($event)"
							class="message-textarea mb-3"
							maxlength="300"
							placeholder="Type your notification message to broadcast to the live site…"
						></textarea>
						<div class="flex gap-2">
							<button
								class="btn-primary flex-1"
								[disabled]="!canSendNotification()"
								(click)="onSendToSiteClicked()"
							>
								<i class="bi bi-send mr-2"></i>
								<span>Send to Site</span>
							</button>
							<button
								class="btn-outline"
								[disabled]="isNotificationSaving()"
								(click)="onClearBannerMessageClicked()"
							>
								<i class="bi bi-x-lg mr-2"></i>
								Clear
							</button>
						</div>
					</div>
				</div>
			</section>
		}
	</main>
</div>

<!-- Toast Notification -->
<div class="toast" [ngClass]="[showToast() ? 'toast-enter' : '']">
	<i class="bi bi-info-circle"></i>
	<span>{{ toastMessage() }}</span>
</div>
